import { useCallback, useEffect, useState } from 'react';
import { apiService, ApiServiceError } from '../../services/apiService';
import type { KeywordList } from '../../types/keywords';

export type DraftProblem = 'conflict' | 'failed' | null;

/**
 * Loads the shared draft (with its etag) and the active list, and saves draft
 * changes with optimistic concurrency. A save that loses against someone
 * else's save sets `problem` to `conflict`; the page then offers a reload.
 */
export function useKeywordVocabulary(accessToken: string | null) {
  const [draft, setDraft] = useState<KeywordList | null>(null);
  const [etag, setEtag] = useState<string | null>(null);
  const [active, setActive] = useState<KeywordList | null>(null);
  const [problem, setProblem] = useState<DraftProblem>(null);

  const reload = useCallback(async () => {
    const [draftResponse, activeList] = await Promise.all([
      apiService.getKeywordDraft(),
      apiService.getActiveKeywords(),
    ]);
    setDraft(draftResponse.draft);
    setEtag(draftResponse.etag);
    setActive(activeList);
    setProblem(null);
  }, []);

  useEffect(() => {
    // Set before the first request; the tabs only mount once the draft is loaded.
    apiService.setAccessToken(accessToken);
    reload().catch(() => setProblem('failed'));
  }, [accessToken, reload]);

  const save = useCallback(
    async (next: KeywordList): Promise<boolean> => {
      try {
        const response = await apiService.saveKeywordDraft(next, etag);
        setDraft(next);
        setEtag(response.etag);
        return true;
      } catch (err) {
        setProblem(err instanceof ApiServiceError && err.status === 409 ? 'conflict' : 'failed');
        return false;
      }
    },
    [etag]
  );

  return { draft, etag, active, problem, reload, save };
}

export type KeywordVocabulary = ReturnType<typeof useKeywordVocabulary>;
